import { FC, useContext, useState } from 'react';
import { AiOutlineExclamationCircle } from '@react-icons/all-files/ai/AiOutlineExclamationCircle';
import { MdCheck } from '@react-icons/all-files/md/MdCheck';

import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';
import { toaster } from 'components/design-system/Toaster';
import { ConditionLabelValueGroup } from 'components/listing_detail/v2/ConditionLabelValueGroup';
import { PayoutBottomSheet } from 'components/offers/PayoutBottomSheet';
import { useModal } from 'context/modal/ModalContext';
import { SellerProfileContext } from 'context/sellerProfile';
import { useHideIntercom } from 'hooks';
import { intercomShowNewMessage } from 'lib/intercom';
import { PurchaseRequest, PurchaseRequestOffer } from 'types';
import { formatPrice, getListingBreakdownPrice } from 'utils/text-helpers';

import { RequestsCountdown } from './components/RequestsCountdown';
import { SellerInventorySlideout } from './SellerInventorySlideout';
import { SubmittedOffersSlideout } from './SubmittedOffersSlideout';

type RequestDescriptionSlideoutProps = {
  purchaseRequest: PurchaseRequest;
  requestOffers?: PurchaseRequestOffer[];
} & SlideoutCommonProps;

export const RequestDescriptionSlideout: FC<RequestDescriptionSlideoutProps> = ({
  purchaseRequest,
  requestOffers = [],
  ...props
}) => {
  const [isPayoutBottomSheetOpen, setIsPayoutBottomSheetOpen] = useState(false);
  const { closeModal, openModal } = useModal();
  const { sellerProfile, sellerLevels } = useContext(SellerProfileContext);
  const isLive = purchaseRequest.status === 'LIVE';

  const openInventorySlideout = () =>
    openModal(<SellerInventorySlideout purchaseRequest={purchaseRequest} closeModal={closeModal} />, true);
  const openSubmittedOffersSlideout = () =>
    openModal(<SubmittedOffersSlideout requestOffers={requestOffers} closeModal={closeModal} />, true);

  const { earnings, commissionCents, minimumTotalFee, sellerFeeCents } = getListingBreakdownPrice(
    {
      priceCents: purchaseRequest.requestedPriceCents
    },
    sellerProfile,
    sellerLevels
  );
  const isUnworn = purchaseRequest.unwornRestriction === 'REQUIRE';

  useHideIntercom();

  let onCancelClick;

  if (purchaseRequest.viewerCanOffer && requestOffers.length > 0) {
    onCancelClick = openInventorySlideout;
  } else if (purchaseRequest.viewerCanOffer) {
    onCancelClick = intercomShowNewMessage;
  } else {
    onCancelClick = () => toaster.info('You’ve reached the maximum number of offers you can submit on this request');
  }

  let submitTitle;

  if (requestOffers.length === 1) {
    submitTitle = 'You already offered a watch';
  } else if (requestOffers.length > 1) {
    submitTitle = `You already offered ${requestOffers.length} watches`;
  } else {
    submitTitle = 'Submit a watch';
  }

  return (
    <Slideout
      title
      bodyClassName='px-0'
      {...props}
      submit={
        isLive
          ? {
              variant: requestOffers.length > 0 ? 'success' : 'primary',
              title: submitTitle,
              onClick: requestOffers.length > 0 ? openSubmittedOffersSlideout : openInventorySlideout
            }
          : undefined
      }
      cancel={{
        title: requestOffers.length > 0 ? 'Submit another' : 'Ask us a question',
        onClick: onCancelClick
      }}
    >
      <>
        {!isLive && (
          <div
            style={{ height: 42 }}
            className='px-4 mb-3 w-100 d-flex fs-14px justify-content-between align-items-center riforma-regular bg-danger-pale text-light'
          >
            <span>The request has already been filled</span>
            <AiOutlineExclamationCircle size={20} />
          </div>
        )}
        <div className='px-4'>
          <div className='d-flex flex-column w-100'>
            {isLive && <RequestsCountdown expiration={purchaseRequest.expiration} className='opacity-60 fs-12px' />}
            <div className='riforma-regular fs-48px text-success'>
              {formatPrice(purchaseRequest.requestedPriceCents)}
            </div>
            <div className='mb-4 riforma-regular'>
              At this price you will net{' '}
              <span
                role='button'
                onClick={() => setIsPayoutBottomSheetOpen(true)}
                className='text-success text-decoration-underline'
              >
                {formatPrice(earnings)}
              </span>
            </div>
            <ConditionLabelValueGroup
              label={<span className='fs-16px'>{isUnworn ? 'Unworn only' : 'Open to pre-owned'}</span>}
              value={
                isUnworn
                  ? 'The buyer is only interested in seeing unworn examples'
                  : 'The buyer is interested in both unworn and pre-owned examples'
              }
            />
            <ConditionLabelValueGroup
              label={<span className='fs-16px'>{purchaseRequest.yearRestriction ? 'Specific year' : 'Any year'}</span>}
              value={
                purchaseRequest.yearRestriction
                  ? `The buyer will only accept examples that are ${purchaseRequest.yearRestriction} or newer`
                  : 'The buyer is open to any year that this model was in production'
              }
            />
            {!isUnworn && (
              <ConditionLabelValueGroup
                label={<span className='fs-16px'>Client reviews first</span>}
                value='Because this is a pre-owned request, your example will be submitted for review with the client. They can accept or reject your example.'
              />
            )}
          </div>
          <ConditionLabelValueGroup
            label={
              <span className='fs-16px text-success'>
                <MdCheck style={{ bottom: 1, position: 'relative' }} />
                <span className='ms-1'>Deposit made</span>
              </span>
            }
            value='The buyer has tied a credit card to this request and made a deposit'
          />
          {isPayoutBottomSheetOpen && (
            <PayoutBottomSheet
              onClose={() => setIsPayoutBottomSheetOpen(false)}
              earnings={earnings}
              commission={commissionCents}
              sellingPrice={purchaseRequest.requestedPriceCents}
              sellerFee={sellerFeeCents}
              minimalFee={minimumTotalFee}
            />
          )}
        </div>
      </>
    </Slideout>
  );
};
